import type { InputNode } from '../types';

export type AnalyzeModuleStore = {
  reportModules: Record<string, Array<{ title: string; content: string }>>;
  markdownModules: Record<string, Array<{ title: string; content: string; markdown: string }>>;
};

/**
 * 把解析结果展开进 config 流。
 *
 * markdown / markdown-name 三态（与 V1 推广分析「粘贴→解析→变表格」体验对齐，
 * 但不照搬 V1 的 `key==='tableData'` 硬编码——对所有 markdown/markdown-name 一视同仁）：
 *   - 解析中（key ∈ parsingKeys）：隐藏原输入框，插入「解析中...」占位（rte-markdown-parsing）
 *   - 解析成功（store.markdownModules[key] 有 module）：隐藏原输入框，只展开 markdown-module 子节点
 *   - 未解析 / 已清空：保留原输入框（用户可重新粘贴）
 *
 * report-url 不走「解析后隐藏」——它解析出报告名后切 name chip 仍需可见可清除，模块追加在后面。
 */
export function buildEffectiveConfig(
  base: InputNode[],
  store: AnalyzeModuleStore,
  parsingKeys: Set<string> = new Set(),
): InputNode[] {
  const result: InputNode[] = [];
  let trimNextLeadingNewline = false;

  for (const node of base) {
    const isMarkdownLike = node.type === 'markdown' || node.type === 'markdown-name';

    if (isMarkdownLike && node.key) {
      const modules = store.markdownModules[node.key];
      const isParsing = parsingKeys.has(node.key);
      const hasModules = !!modules && modules.length > 0;

      // markdown-name：始终保留原输入框（店铺名），解析状态追加在后面
      if (node.type === 'markdown-name') {
        result.push(node);
        if (isParsing && !hasModules) {
          result.push({
            type: 'text',
            className: 'rte-markdown-parsing',
            text: '解析中...',
            exclusionGroup: node.exclusionGroup,
          });
        }
        if (hasModules) {
          modules.forEach((mod, i) => {
            result.push({
              type: 'markdown-module',
              key: node.key,
              moduleIndex: i,
              text: mod.title,
              placeholder: '可编辑，内容不能为空',
              exclusionGroup: node.exclusionGroup,
              required: node.required,
            });
          });
          trimNextLeadingNewline = true;
        }
        continue;
      }

      // markdown：解析中/成功时隐藏原输入框
      if (isParsing && !hasModules) {
        result.push({
          type: 'text',
          key: node.key,
          className: 'rte-markdown-parsing',
          text: '解析中...',
          exclusionGroup: node.exclusionGroup,
        });
        continue;
      }

      if (hasModules) {
        modules.forEach((mod, i) => {
          result.push({
            type: 'markdown-module',
            key: node.key,
            moduleIndex: i,
            text: mod.title,
            placeholder: '可编辑，内容不能为空',
            exclusionGroup: node.exclusionGroup,
            required: node.required,
          });
        });
        trimNextLeadingNewline = true;
        continue;
      }

      // 未解析 / 已清空：保留原输入框
      result.push(node);
      continue;
    }

    // markdown-module 是 block 级元素，去掉后续第一个以 \n 开头的文本节点的 \n 避免多余空行
    // 中间的 separator 等被互斥隐藏的节点不重置标记，让 trim 穿透到目标
    if (trimNextLeadingNewline) {
      if (node.type === 'text' && node.text?.startsWith('\n')) {
        result.push({ ...node, text: node.text.slice(1) });
        trimNextLeadingNewline = false;
        continue;
      }
      if (node.exclusionGroup === 'separator') {
        result.push(node);
        continue;
      }
    }

    result.push(node);

    if (node.type === 'report-url' && node.key && store.reportModules[node.key]) {
      store.reportModules[node.key].forEach((mod, i) => {
        result.push({
          type: 'report-module',
          key: node.key,
          moduleIndex: i,
          text: mod.title,
          exclusionGroup: node.exclusionGroup,
          required: node.required,
        });
      });
    }
  }
  return result;
}

export const EMPTY_MODULE_STORE: AnalyzeModuleStore = {
  reportModules: {},
  markdownModules: {},
};
